import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Privacy & Confidentiality Policy</h1>
          <p className="text-slate-300 text-lg">Effective Date: 01/04/2025</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none">
            <div className="space-y-12">
              {/* Introduction */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">1. Introduction</h2>
                <p className="text-slate-700 leading-relaxed mt-4">
                  Visrupt ("Company," "we," "our," or "us") is committed to maintaining the confidentiality and security of all client-related information. This Privacy & Confidentiality Policy governs how we handle client data, project details, and proprietary trading strategies. By engaging with our services, clients acknowledge and agree to the terms outlined herein.
                </p>
              </div>

              {/* Confidentiality of Client Information */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">2. Confidentiality of Client Information</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    2.1. The Company does not disclose, share, or distribute any client information, project details, trading strategies, or proprietary data to third parties, businesses, or individuals without the client's explicit written consent.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    2.2. All project-related discussions, specifications, and deliverables are treated as confidential and are only accessible to authorized personnel within the Company.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    2.3. Any data provided by the client for development purposes shall be securely stored and retained only for the duration necessary to complete the project. Upon project completion, the Company may delete such data unless otherwise required for contractual or legal purposes.
                  </p>
                </div>
              </div>

              {/* Non-Disclosure Agreement */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">3. Non-Disclosure Agreement (NDA)</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    3.1. The Company offers NDAs as an add-on service to provide additional legal protection for confidential information.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    3.2. Clients must explicitly request an NDA prior to disclosing any project details. In the absence of an NDA, the Company will adhere to its internal confidentiality policies but provides no additional legal guarantees.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    3.3. If an NDA is executed, the terms of the NDA shall govern the handling of confidential information in addition to this Privacy & Confidentiality Policy.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    3.4. <Link to="/nda-policy" className="text-blue-600 hover:text-blue-800">Click here to know more about Non-Disclosure Agreement (NDA)</Link>
                  </p>
                </div>
              </div>

              {/* Data Collection and Use */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">4. Data Collection and Use</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    4.1. The Company does not collect, store, or use client data for marketing, analytics, or any third-party purposes.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    4.2. The Company does not sell, rent, trade, or disclose client information to any external entity.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    4.3. Any personal or business information provided by the client is used solely for the purpose of delivering the agreed-upon services.
                  </p>
                </div>
              </div>

              {/* Security Measures */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">5. Security Measures</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    5.1. The Company employs industry-standard security measures to prevent unauthorized access, disclosure, or modification of client information.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    5.2. Access to project-related information is strictly limited to authorized personnel directly involved in the execution of the project.
                  </p>
                </div>
              </div>

              {/* Amendments and Updates */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">6. Amendments and Updates</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    6.1. The Company reserves the right to update or modify this Privacy & Confidentiality Policy at its sole discretion. Any changes will be posted on the Company's website, and continued use of our services after such modifications constitutes acceptance of the revised terms.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    6.2. Clients will be notified of significant changes that materially affect their rights or obligations under this policy.
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">7. Contact Information</h2>
                <div className="mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    For any inquiries regarding this Privacy & Confidentiality Policy or to request an NDA, clients may contact us at:
                  </p>
                  <div className="mt-4 space-y-2">
                    <p className="text-slate-700">
                      Email:{' '}
                      <a href="mailto:support@visrupt.in" className="text-blue-600 hover:text-blue-800">
                        support@visrupt.in
                      </a>
                    </p>
                    <p className="text-slate-700">
                      Contact:{' '}
                      <a href="tel:+917522979568" className="text-blue-600 hover:text-blue-800">
                        +91 7522979568
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Acknowledgment */}
              <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                <p className="text-slate-700 font-medium">
                  By engaging with Visrupt's services, clients acknowledge that they have read, understood, and agreed to the terms of this Privacy & Confidentiality Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;